import React, { useRef, useCallback, LegacyRef } from 'react';
import cn from 'classnames';
import { Icon } from 'ht-styleguide';

/* Store */
import useChatStore, { ChatState } from '@store/client/store.chat';

/* Components & Integrations */
import DarkOverlay from '@components/UI/DarkOverlay';
import PhoneDisplayLink from '@components/PhoneDisplayLink';
import ChatLink from '@integrations/gladly/Chat/ChatLink';
import Chat from '@integrations/gladly/Chat/Gladly.methods';

/* Hooks */
import useEventListener from '@hooks/useEventListener';
import { Portal } from '@hooks/useCreatePortal';
import usePrevious from '@hooks/usePrevious';

/* Constants */
import { HEADER_PORTAL_ID } from '@constants/base';

/* Types */
import { Dispatcher } from 'types/base.types';

/* Styles */
import styles from './chatpreviewicon.module.scss';

type ChatCallDropdownProps = {
  showDropdown: boolean;
  toggleState: Dispatcher<boolean>;
};

const ChatCallDropdown = ({ showDropdown, toggleState }: ChatCallDropdownProps) => {
  const unreadMessages = useChatStore((state: Pick<ChatState, 'chat'>) => state.chat.unreadMessages);
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const previousShowDropdown = usePrevious<boolean>(showDropdown);

  const handleOutsideClick = useCallback(
    (e: React.ChangeEvent<HTMLDivElement>) => {
      /*
      Clicks in the Header are also detected so we need to omit the event when the
      user clicks on <ChatIcon />
    */
      const outsideClicked = !actionsRef.current?.contains(e.target);
      const clickedFromIcon = e.target.className.includes('ht-chat');
      if (!clickedFromIcon && showDropdown && outsideClicked) {
        toggleState(false);
      } else if (clickedFromIcon && showDropdown) {
        toggleState(!previousShowDropdown);
      }
    },
    [showDropdown, previousShowDropdown, toggleState],
  );

  useEventListener({ eventName: 'click', handler: handleOutsideClick });

  const openChatCallback = () => {
    toggleState(false);
  };
  const openChat = useCallback(() => {
    Chat.open();
  }, []);

  const call = useCallback(() => {
    toggleState(false);
  }, [toggleState]);

  const containerStyles = cn(styles.container, {
    [styles.show]: showDropdown,
  });

  if (!showDropdown) return null;

  return (
    <Portal id={HEADER_PORTAL_ID}>
      <div className={containerStyles}>
        <div ref={actionsRef as LegacyRef<HTMLDivElement>} className={styles.actionsWrapper}>
          <div className={styles.buttonWrapper}>
            <button type="button" className={`plainButton ${styles.chatIcon}`} onClick={openChat}>
              {unreadMessages && <div className={styles.unreadMessages} />}
              <ChatLink handleLoadedCallback={openChatCallback}>
                <div className={styles.chatText}>
                  <Icon name="message" className={styles.icon} />
                  <h4 className={styles.header}>Chat</h4>
                </div>
              </ChatLink>
            </button>
          </div>
          <div className={styles.buttonWrapper}>
            <button type="button" className="plainButton" onClick={call}>
              <PhoneDisplayLink icon={<Icon name="phone" className={styles.icon} />} />
              <h4 className={styles.header}>Call</h4>
            </button>
          </div>
        </div>
        <DarkOverlay absolute show={showDropdown} className={styles.darkOverlay} />
      </div>
    </Portal>
  );
};

export default ChatCallDropdown;
